'use client';

import React from 'react';
import { leaveRequests, leaveTypeChartData } from '@/lib/mockData';
import { CalendarCheck, Clock, CheckCircle2, Users, TrendingUp } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

export default function LeaveDashboardTab() {
  const total = leaveRequests?.length;
  const pending = leaveRequests?.filter((r) => !r?.approvedAt)?.length;
  const approved = leaveRequests?.filter((r) => r?.approvedAt)?.length;
  const departments = new Set(leaveRequests.map((r) => r.department))?.size;

  const kpis = [
    {
      label: 'Total Requests',
      value: total,
      icon: <CalendarCheck size={20} className="text-primary" />,
      bg: 'bg-primary/10',
    },
    {
      label: 'Pending Approval',
      value: pending,
      icon: <Clock size={20} className="text-warning" />,
      bg: 'bg-warning/10',
    },
    {
      label: 'Approved',
      value: approved,
      icon: <CheckCircle2 size={20} className="text-success" />,
      bg: 'bg-success/10',
    },
    {
      label: 'Departments',
      value: departments,
      icon: <Users size={20} className="text-accent" />,
      bg: 'bg-accent/10',
    },
  ];

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis?.map((k) => (
          <div
            key={k?.label}
            className="bg-card border border-border rounded-xl shadow-card px-5 py-4 flex items-center gap-4"
          >
            <div className={`${k?.bg} rounded-lg p-2.5 shrink-0`}>{k?.icon}</div>
            <div>
              <p className="text-2xl font-700 text-foreground tabular-nums">{k?.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{k?.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Chart + recent table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Leave type chart */}
        <div className="bg-card border border-border rounded-xl shadow-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-primary" />
            <h2 className="text-sm font-700 text-foreground">Leave by Type</h2>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={leaveTypeChartData} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="type" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {leaveTypeChartData?.map((entry, i) => (
                  <Cell key={`cell-${i}`} fill={entry?.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Recent requests */}
        <div className="bg-card border border-border rounded-xl shadow-card p-5">
          <h2 className="text-sm font-700 text-foreground mb-4">Recent Leave Requests</h2>
          <div className="space-y-2">
            {leaveRequests?.slice(0, 5)?.map((req) => (
              <div
                key={req?.id}
                className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0"
              >
                <div className="min-w-0">
                  <p className="text-sm font-600 text-foreground truncate">{req?.employeeId}</p>
                  <p className="text-xs text-muted-foreground">{req?.department} · {req?.typeOfLeave}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-muted-foreground tabular-nums">{req?.startDate}</p>
                  <span
                    className={`inline-block text-[10px] font-600 px-2 py-0.5 rounded-full mt-0.5 ${
                      req?.approvedAt
                        ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                    }`}
                  >
                    {req?.approvedAt ? 'Approved' : 'Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
